/**
 * @fileoverview Select padrão do app: o campo e o menu que abre embaixo dele,
 * nos dois temas. Aceita as props de um TextField select (label, helperText,
 * size, fullWidth, disabled, error, required, InputLabelProps), então troca um
 * pelo outro sem mexer no resto. `modernFieldStyles` dá a mesma cara aos
 * campos de texto de um formulário que usa este select.
 */

import React from "react";
import { FormControl, FormHelperText, InputLabel, Select, makeStyles } from "@material-ui/core";
import { unstable_useId as useId } from "@material-ui/core/utils";

// A borda é a do contorno do MUI (e não uma borda no campo): assim o rótulo
// no alto abre o espaço dele na linha, como num TextField.
export const modernFieldStyles = (theme) => {
  const dark = theme.palette.type === "dark";
  const text = dark ? "#f8fafc" : "#0f172a";
  return {
    "& label.MuiInputLabel-outlined": { color: dark ? "#94a3b8" : "#64748b" },
    "& label.MuiInputLabel-outlined.Mui-focused": { color: dark ? "#a5b4fc" : "#3D5CFD" },
    "& label.MuiInputLabel-outlined.Mui-error": { color: theme.palette.error.main },
    "& label.MuiInputLabel-outlined.Mui-disabled": { opacity: 0.6 },
    "& .MuiOutlinedInput-root": {
      borderRadius: 14,
      background: dark ? "rgba(255, 255, 255, 0.05)" : "rgba(0, 0, 0, 0.02)",
      color: text,
      transition: "all 0.3s cubic-bezier(0.4, 0, 0.2, 1)",
      "& .MuiInputBase-input": { color: text },
      "& .MuiSelect-select:focus": { backgroundColor: "transparent" },
      "& .MuiSelect-icon": { color: dark ? "#94a3b8" : "#64748b" },
      "& .MuiOutlinedInput-notchedOutline": {
        borderWidth: 1,
        borderColor: dark ? "rgba(91, 124, 255, 0.15)" : "rgba(15, 23, 42, 0.2)",
        transition: "border-color 0.2s",
      },
      "&:hover:not(.Mui-disabled)": {
        background: dark ? "rgba(255, 255, 255, 0.08)" : "rgba(61, 92, 253, 0.04)",
      },
      "&:hover .MuiOutlinedInput-notchedOutline": {
        borderColor: dark ? "rgba(91, 124, 255, 0.25)" : "rgba(61, 92, 253, 0.4)",
      },
      "&.Mui-focused": {
        background: dark ? "rgba(255, 255, 255, 0.08)" : "#ffffff",
        boxShadow: "0 0 0 1px rgba(91, 124, 255, 0.15)",
      },
      "&.Mui-focused .MuiOutlinedInput-notchedOutline": {
        borderWidth: 1,
        borderColor: dark ? "rgba(91, 124, 255, 0.4)" : "rgba(61, 92, 253, 0.6)",
      },
      "&.Mui-error .MuiOutlinedInput-notchedOutline": { borderColor: theme.palette.error.main },
      "&.Mui-disabled": { opacity: 0.6 },
      "&.Mui-disabled .MuiOutlinedInput-notchedOutline": {
        borderColor: dark ? "rgba(91, 124, 255, 0.15)" : "rgba(15, 23, 42, 0.2)",
      },
    },
  };
};

const useStyles = makeStyles((theme) => ({
  root: modernFieldStyles(theme),
  menuPaper: {
    marginTop: theme.spacing(1),
    borderRadius: 14,
    background: theme.palette.type === "dark"
      ? "linear-gradient(135deg, #121213 0%, #18181b 100%)"
      : "#ffffff",
    border: theme.palette.type === "dark"
      ? "1px solid rgba(91, 124, 255, 0.18)"
      : "1px solid rgba(15, 23, 42, 0.08)",
    boxShadow: theme.palette.type === "dark"
      ? "0 18px 40px rgba(0, 0, 0, 0.4)"
      : "0 18px 40px rgba(15, 23, 42, 0.12)",
  },
  menuList: {
    padding: theme.spacing(0.75),
    "& .MuiMenuItem-root": {
      borderRadius: 10,
      color: theme.palette.type === "dark" ? "#e5e7eb" : "#0f172a",
      fontSize: "0.9rem",
      minHeight: 38,
    },
    "& .MuiMenuItem-root:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.16)"
        : "rgba(61, 92, 253, 0.08)",
    },
    "& .MuiMenuItem-root.Mui-selected": {
      background: theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.22)"
        : "rgba(61, 92, 253, 0.12)",
    },
    "& .MuiMenuItem-root.Mui-selected:hover": {
      background: theme.palette.type === "dark"
        ? "rgba(91, 124, 255, 0.28)"
        : "rgba(61, 92, 253, 0.16)",
    },
  },
}));

const ModernSelect = ({
  id: idProp,
  label,
  helperText,
  children,
  className,
  size,
  fullWidth,
  disabled,
  error,
  required,
  InputLabelProps,
  FormHelperTextProps,
  SelectDisplayProps,
  formControlProps,
  MenuProps,
  ...props
}) => {
  const classes = useStyles();
  const id = useId(idProp);
  const labelId = label && id ? `${id}-label` : undefined;
  const helperTextId = helperText && id ? `${id}-helper-text` : undefined;
  // O espaço na linha da borda tem que caber o asterisco do rótulo.
  const obrigatorio = required ?? formControlProps?.required;
  const mergedMenuProps = {
    ...MenuProps,
    getContentAnchorEl: MenuProps?.getContentAnchorEl ?? null,
    anchorOrigin: MenuProps?.anchorOrigin ?? { vertical: "bottom", horizontal: "left" },
    transformOrigin: MenuProps?.transformOrigin ?? { vertical: "top", horizontal: "left" },
    PaperProps: {
      ...(MenuProps?.PaperProps || {}),
      className: [classes.menuPaper, MenuProps?.PaperProps?.className].filter(Boolean).join(" "),
    },
    MenuListProps: {
      ...(MenuProps?.MenuListProps || {}),
      className: [classes.menuList, MenuProps?.MenuListProps?.className].filter(Boolean).join(" "),
    },
  };

  return (
    <FormControl
      variant="outlined"
      className={`${classes.root} ${className || ''}`}
      size={size}
      fullWidth={fullWidth}
      disabled={disabled}
      error={error}
      required={required}
      {...formControlProps}
    >
      {label && (
        <InputLabel id={labelId} {...InputLabelProps}>
          {label}
        </InputLabel>
      )}
      <Select
        id={id}
        labelId={labelId}
        label={label && obrigatorio ? <>{label}&nbsp;*</> : label}
        notched={InputLabelProps?.shrink}
        MenuProps={mergedMenuProps}
        SelectDisplayProps={helperTextId ? { "aria-describedby": helperTextId, ...SelectDisplayProps } : SelectDisplayProps}
        {...props}
      >
        {children}
      </Select>
      {helperText && (
        <FormHelperText id={helperTextId} {...FormHelperTextProps}>
          {helperText}
        </FormHelperText>
      )}
    </FormControl>
  );
};

export default ModernSelect;
